/**
 * Role illustrations: the curated pictures a role card shows.
 *
 * A role stores a KEY, never a URL or an upload. The lists below mirror the
 * API's (`apps/api/src/modules/roles/role-artwork.ts`), which is the rule — the
 * server refuses any key outside it. The two copies, the files and their
 * manifest are held together by `lib/roles/__tests__/role-artwork-assets.guard.spec.ts`.
 *
 * The files are committed under `public/static/roles/artwork/` and written by
 * `apps/api/scripts/generate-role-artwork.ts` (the Vishing artwork recipe).
 */

/** The built-in roles, in the order the Roles page shows them. */
export const BUILT_IN_ROLE_KEYS = [
  'company-owner',
  'company-admin',
  'workspace-admin',
  'workspace-editor',
  'workspace-viewer',
] as const;
export type BuiltInRoleKey = (typeof BUILT_IN_ROLE_KEYS)[number];

/** Every key a role may store: the built-in pictures first, then the presets (API order). */
export const ROLE_ARTWORK_KEYS = [
  'role-owner',
  'role-company-admin',
  'role-workspace-admin',
  'role-editor',
  'role-viewer',
  'role-analyst',
  'role-trainer',
  'role-campaign-manager',
  'role-auditor',
  'role-communicator',
  'role-champion',
  'role-helpdesk',
  'role-executive',
] as const;
export type RoleArtworkKey = (typeof ROLE_ARTWORK_KEYS)[number];

/** The picture each built-in role carries. */
export const BUILT_IN_ROLE_ARTWORK: Record<BuiltInRoleKey, RoleArtworkKey> = {
  'company-owner': 'role-owner',
  'company-admin': 'role-company-admin',
  'workspace-admin': 'role-workspace-admin',
  'workspace-editor': 'role-editor',
  'workspace-viewer': 'role-viewer',
};

/** The extra pictures a new role can choose from, named for the job they suggest. */
export const ROLE_PRESET_ARTWORK_KEYS = [
  'role-analyst',
  'role-trainer',
  'role-campaign-manager',
  'role-auditor',
  'role-communicator',
  'role-champion',
  'role-helpdesk',
  'role-executive',
] as const satisfies readonly RoleArtworkKey[];

const KEY_SET: ReadonlySet<string> = new Set(ROLE_ARTWORK_KEYS);

export function isRoleArtworkKey(value: unknown): value is RoleArtworkKey {
  return typeof value === 'string' && KEY_SET.has(value);
}

/**
 * The picture a key names, or null. Only a key from the closed list ever
 * becomes a path, so nothing a tenant types or an API returns can reach `src`.
 */
export function roleArtworkSrc(key: string | null | undefined): string | null {
  return isRoleArtworkKey(key) ? `/static/roles/artwork/${key}.webp` : null;
}

/**
 * The translation key of a picture's short label: `role-campaign-manager` →
 * `roles.artwork.campaignManager`.
 */
export function roleArtworkLabelKey(key: RoleArtworkKey): string {
  const camel = key
    .replace(/^role-/, '')
    .replace(/-([a-z0-9])/g, (_, letter: string) => letter.toUpperCase());
  return `roles.artwork.${camel}`;
}
